import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FileText, Download as DLIcon, Lock } from "lucide-react";
import LeadGateModal from "@/components/LeadGateModal";

const CATS = ["All", "Company Profile", "Catalogues", "Brochures", "Manuals", "Forms"];

export default function Downloads() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("All");
    const [gateFor, setGateFor] = useState(null);
    const [unlocked, setUnlocked] = useState(() => {
        try { return JSON.parse(localStorage.getItem("peppa_unlocked") || "[]"); } catch { return []; }
    });

    useEffect(() => {
        api.get("/downloads").then((r) => setItems(r.data)).catch(() => {});
    }, []);

    const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

    function isUnlocked(id) { return unlocked.includes(id); }

    function handleClick(d) {
        if (isUnlocked(d.id)) {
            openFile(d);
            return;
        }
        setGateFor(d);
    }

    function openFile(d) {
        window.open(d.file_url, "_blank");
    }

    function onUnlock() {
        const id = gateFor.id;
        const next = [...unlocked, id];
        setUnlocked(next);
        localStorage.setItem("peppa_unlocked", JSON.stringify(next));
        openFile(gateFor);
        setGateFor(null);
    }

    return (
        <div data-testid="downloads-page">
            <section className="bg-ink text-white py-24 md:py-28">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    <div className="overline text-primary mb-4">Downloads</div>
                    <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1] tracking-tight">
                        Documents & resources
                    </h1>
                    <p className="text-sm text-white/60 mt-4">Free, instant access — quick details required.</p>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
                <div className="flex flex-wrap gap-2 mb-8">
                    {CATS.map((c) => (
                        <button
                            key={c}
                            onClick={() => setFilter(c)}
                            className={`px-4 py-2 text-sm font-semibold border transition-colors ${
                                filter === c ? "bg-primary text-white border-primary" : "border-border hover:border-primary"
                            }`}
                            data-testid={`download-filter-${c.toLowerCase().replace(/\s/g, '-')}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                <div className="space-y-2">
                    {filtered.map((d) => {
                        const locked = !isUnlocked(d.id);
                        return (
                            <div key={d.id} className="flex items-center gap-4 p-4 bg-white border border-border hover:border-primary transition-colors" data-testid={`download-item-${d.id}`}>
                                <div className="w-12 h-12 bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                    <FileText className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-semibold truncate">{d.title}</div>
                                    <div className="text-xs text-muted-foreground truncate">{d.description}</div>
                                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">
                                        {d.file_type.toUpperCase()} · {d.file_size}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleClick(d)}
                                    className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary-hover transition-colors"
                                    data-testid={`download-btn-${d.id}`}
                                >
                                    {locked ? <Lock className="w-3.5 h-3.5" /> : <DLIcon className="w-3.5 h-3.5" />}
                                    {locked ? "Unlock" : "Download"}
                                </button>
                            </div>
                        );
                    })}
                    {filtered.length === 0 && (
                        <div className="bg-surface border border-border p-8 text-center text-muted-foreground">No documents in this category.</div>
                    )}
                </div>
            </section>

            {gateFor && (
                <LeadGateModal
                    download={gateFor}
                    onClose={() => setGateFor(null)}
                    onUnlock={onUnlock}
                />
            )}
        </div>
    );
}
